import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import { OrganizationServerResponse } from "../../models/OrganisationServerResponse";
import api from "../../helpers/axios";
import { crrObjClone } from "../../helpers/utils";
import { OrganizationPaginated } from "../../models/OrganizationPaginated";
import { Organization } from "../../models/Organization";
import { showError } from "../../helpers/Notifications/Notification";
import { clearOverlayCache } from "../../pages/IncidentResponse/api/overlaysApi";

export const getOrganizationThunk = createAsyncThunk(
  "admin/organization",
  /**
   *
   * @param {string} searchString
   * @param {number} page
   * @param {number} size
   * * @param {number} stateAdminId
   */
  async ({ searchString, page, size, stateAdminId }, thunkAPI) => {
    try {
      const response = stateAdminId
        ? await api.get(
            `admin/organization/?search=${encodeURIComponent(searchString)}&page=${page}&page_size=${size}&state_admin_id=${stateAdminId}`
          )
        : await api.get(
            `admin/organization/?search=${encodeURIComponent(searchString)}&page=${page}&page_size=${size}`
          );

      /**
       * convert snack-case to camel-case for the sake
       * of convention
       */
      /**
       * @type {OrganizationServerResponse}
       */
      const properStyledResponse = crrObjClone(
        new OrganizationServerResponse(),
        response.data
      );

      properStyledResponse.data.organizationCount =
        response.data.data.organization_count;
      properStyledResponse.data.shouldOrgListUpdate = !!(
        stateAdminId ||
        searchString ||
        page > 1 ||
        response.data.data.organization_count === 0
      ); //update oragnisation list if it is called from membership portal or has a searchstring
      properStyledResponse.data.organizationData = (
        response.data.data.organization_data || []
      ).map((organization) => {
        /**
         * @type {Organization}
         */
        const org = {
          criticalPocEmail: organization.critical_POC_email,
          criticalPocName: organization.critical_POC_name,
          criticalPocPhone: organization.critical_POC_phone,
          criticalService: organization.critical_service,
          dateOfCrr: organization.date_of_CRR,
          facilitatorEmail: organization.facilitator_email,
          facilitatorName: organization.facilitator_name,
          facilitatorPhone: organization.facilitator_phone,
          id: organization.id,
          isActive: organization.is_active,
          isPaid: organization.is_paid,
          locationCity: organization.location_city,
          locationState: organization.location_state,
          state: organization.state,
          logo: organization.logo,
          organizationName: organization.organization_name,
          sector: organization.sector,
          isActivationLoading: false,
          isSendingCredentials: false,
          isDeleting: false,
          facilitatorTitle: organization.facilitator_title,
          facilitatorTitleId: organization.facilitator_title_id,
          organization_type: organization.organization_type,
        };

        return org;
      });

      return properStyledResponse;
    } catch (error) {
      return error;
    }
  }
);

export const patchActivationStatus = createAsyncThunk(
  "admin/organization/changeActivationStatus/",
  /**
   *
   * @param {Object} payload
   * @param {Organization} payload.organization
   */
  async (payload) => {
    try {
      const response = await api.patch(`admin/organization/activate/`, {
        email: payload.organization.facilitatorEmail,
        is_active: !payload.organization.isActive,
      });
      return payload.organization;
    } catch (error) {
      return error;
    }
  }
);

export const addOrganizationThunk = createAsyncThunk(
  "admin/addOrganization/",
  /**
   *
   * @param {Organization} organization
   */
  async (organization) => {
    try {
      const response = await api.post("admin/organization/", {
        name: organization.organizationName,
        email: organization.facilitatorEmail,
        facilitator_name: organization.facilitatorName,
        facilitator_title: organization.facilitatorTitleId,
        organization_type_id: organization.organization_type_id,
        send_credentials: organization.sendEmail,
        state_admin_id: organization.stateAdminId,
        state: organization.state,
      });

      return response.data;
    } catch (error) {
      return { error };
    }
  }
);

export const updateOrganizationThunk = createAsyncThunk(
  "admin/updateOrganization/",
  /**
   *
   * @param {Organization} organization
   */
  async (organization) => {
    try {
      const response = await api.patch("admin/organization/", {
        name: organization.organizationName,
        email: organization.facilitatorEmail,
        facilitator_name: organization.facilitatorName,
        facilitator_title: organization.facilitatorTitleId,
        current_email: organization.currentEmail,
        state_admin_id: organization.stateAdminId,
        state: organization.state,
      });

      // Jurisdiction may have changed (e.g. OH→OTRP); drop FE overlay cache.
      clearOverlayCache();

      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const deleteOrganizationThunk = createAsyncThunk(
  "admin/deleteOrganization",
  /**
   *
   * @param {Organization} organization
   */
  async (organization) => {
    try {
      const response = await api.delete(
        `admin/organization/?id=${organization.id}`
      );

      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const sendOrganizationCredentialsThunk = createAsyncThunk(
  "admin/organization/send-credentials=",
  /**
   *
   * @param {Organization} organization
   */
  async (organization) => {
    try {
      const response = await api.patch(
        `admin/organization/send-credentials/?id=${organization.id}`
      );

      return response.data;
    } catch (error) {
      return error;
    }
  }
);

const organizationListSlice = createSlice({
  name: "organizationList",
  initialState: {
    isLoading: false,
    isAddOrEditLoading: false,
    organizationServerResponse: new OrganizationServerResponse(),
    error: null,
    titles: [],
  },
  reducers: {
    /**
     *
     * @param {{
     *  isLoading: boolean,
     *  isAddOrEditLoading: boolean,
     *  organizationServerResponse: OrganizationServerResponse,
     *  error: *
     * }} state
     * @param {Object} action
     * @param {Organization} action.payload
     */
    setActivationIsloading: (state, action) => {
      const data = state.organizationServerResponse;
      const orgData = data.data.organizationData.map((organization) => {
        if (organization.id === action.payload.id) {
          organization.isActivationLoading = true;
        }

        return organization;
      });
      data.data.organizationData = orgData;
      state.organizationServerResponse = { ...data };
    },
    /**
     *
     * @param {{
     *  isLoading: boolean,
     *  isAddOrEditLoading: boolean,
     *  organizationServerResponse:
     *  OrganizationServerResponse,
     *  error: *
     * }} state
     * @param {Object} action
     * @param {Organization} action.payload
     */
    setActivationIsNotloading: (state, action) => {
      const data = state.organizationServerResponse;
      const orgData = data.data.organizationData.map((organization) => {
        if (organization.id === action.payload.id) {
          organization.isActivationLoading = false;
        }

        return organization;
      });
      data.data.organizationData = orgData;
      state.organizationServerResponse = { ...data };
    },

    setOrganizationIsDeleting: (state, action) => {
      const data = state.organizationServerResponse;
      const orgData = data.data.organizationData.map((organization) => {
        if (organization.id === action.payload.id) {
          organization.isDeleting = true;
        }

        return organization;
      });
      data.data.organizationData = orgData;
      state.organizationServerResponse = { ...data };
    },

    setOrganizationIsNotDeleting: (state, action) => {
      const data = state.organizationServerResponse;
      const orgData = data.data.organizationData.map((organization) => {
        if (organization.id === action.payload.id) {
          organization.isDeleting = false;
        }

        return organization;
      });
      data.data.organizationData = orgData;
      state.organizationServerResponse = { ...data };
    },

    setOrganizationIsSendingCredentails: (state, action) => {
      const data = state.organizationServerResponse;
      const orgData = data.data.organizationData.map((organization) => {
        if (organization.id === action.payload.id) {
          organization.isSendingCredentials = true;
        }

        return organization;
      });
      data.data.organizationData = orgData;
      state.organizationServerResponse = { ...data };
    },

    setOrganizationIsNotSendingCredentails: (state, action) => {
      const data = state.organizationServerResponse;
      const orgData = data.data.organizationData.map((organization) => {
        if (organization.id === action.payload.id) {
          organization.isSendingCredentials = false;
        }

        return organization;
      });
      data.data.organizationData = orgData;
      state.organizationServerResponse = { ...data };
    },
  },
  extraReducers: {
    [getOrganizationThunk.pending]: (state) => {
      state.isLoading = true;
      state.organizationServerResponse = {};
    },
    [getOrganizationThunk.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.organizationServerResponse = action?.payload;
    },
    [getOrganizationThunk.rejected]: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    [patchActivationStatus.rejected]: (state) => {
      showError("Failed to Change Activation Status");
    },
    [patchActivationStatus.fulfilled]: (state, action) => {
      const data = state.organizationServerResponse;
      const orgData = data.data.organizationData.map((organization) => {
        if (organization.id === action.payload.id) {
          organization.isActive = !action.payload.isActive;
        }

        return organization;
      });
      data.data.organizationData = orgData;
      state.organizationServerResponse = { ...data };
    },
    [addOrganizationThunk.pending]: (state) => {
      state.isAddOrEditLoading = true;
    },
    [addOrganizationThunk.fulfilled]: (state) => {
      state.isAddOrEditLoading = false;
    },
    [addOrganizationThunk.rejected]: (state) => {
      state.isAddOrEditLoading = false;
    },
    [updateOrganizationThunk.pending]: (state) => {
      state.isAddOrEditLoading = true;
    },
    [updateOrganizationThunk.fulfilled]: (state) => {
      state.isAddOrEditLoading = false;
    },
    [updateOrganizationThunk.rejected]: (state) => {
      state.isAddOrEditLoading = false;
    },
  },
});

export const organizationSelector = (state) => state.superAdminOrganizations;
export const {
  setActivationIsloading,
  setActivationIsNotloading,
  setOrganizationIsDeleting,
  setOrganizationIsNotDeleting,
  setOrganizationIsNotSendingCredentails,
  setOrganizationIsSendingCredentails,
} = organizationListSlice.actions;
export default organizationListSlice.reducer;
